'use client'

import { useEffect, useState } from 'react'
import { apiFetch } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { toast } from 'sonner'
import type { AppConfig } from '@/types/monthly'
import { Settings, Save, IndianRupee, Clock, Award } from 'lucide-react'
import { GlassCard, GlassCardHeader, GlassCardTitle } from '@/components/ui/glass-card'
import { SectionHeader } from '@/components/ui/section-header'
import { TabLoader } from '@/components/ui/loading'
import { PRIMARY_BTN } from '@/components/ui/button-styles'

export function PerfSettings() {
  const [hourlyRate, setHourlyRate] = useState<string>('100')
  const [taskBonus, setTaskBonus] = useState<string>('50')
  const [standardHoursPerDay, setStandardHoursPerDay] = useState<string>('8')
  const [officeCheckInTime, setOfficeCheckInTime] = useState<string>('10:00')
  const [weights, setWeights] = useState<AppConfig['weights']>({ hours: 0.4, tasks: 0.3, ai: 0.3 })
  const [loading, setLoading] = useState(false)
  // `loading` gates the Save button; this tracks the initial config fetch so the
  // form isn't rendered blank-but-interactive before values arrive.
  const [fetching, setFetching] = useState(true)

  useEffect(() => {
    apiFetch('/api/config').then(r => r.json()).then(d => {
      if (!d?.config) return
      const c: AppConfig = d.config
      setHourlyRate(String(c.hourlyRate ?? 100))
      setTaskBonus(String(c.taskBonus ?? 50))
      setStandardHoursPerDay(String(c.standardHoursPerDay ?? 8))
      setOfficeCheckInTime(c.officeCheckInTime ?? '10:00')
      if (c.weights) setWeights(c.weights)
    }).finally(() => setFetching(false))
  }, [])

  const sum = weights.hours + weights.tasks + weights.ai

  const parseNonNegFinite = (raw: string, field: string): { ok: true; value: number } | { ok: false; error: string } => {
    if (raw.trim() === '') return { ok: false, error: `${field} is required` }
    const n = Number(raw)
    if (!Number.isFinite(n)) return { ok: false, error: `${field} must be a number` }
    if (n < 0) return { ok: false, error: `${field} must be >= 0` }
    return { ok: true, value: n }
  }

  const save = async () => {
    if (Math.abs(sum - 1) > 0.001) return toast.error('Weights must sum to 1.0')

    const hr = parseNonNegFinite(hourlyRate, 'Hourly rate')
    if (!hr.ok) return toast.error(hr.error)
    const tb = parseNonNegFinite(taskBonus, 'Task bonus')
    if (!tb.ok) return toast.error(tb.error)
    const sh = parseNonNegFinite(standardHoursPerDay, 'Standard hours / day')
    if (!sh.ok) return toast.error(sh.error)
    if (sh.value <= 0 || sh.value > 24) return toast.error('Standard hours / day must be between 0 and 24')
    if (!/^\d{2}:\d{2}$/.test(officeCheckInTime)) return toast.error('Office check-in time must be valid')

    setLoading(true)
    try {
      const payload: AppConfig = {
        hourlyRate: hr.value,
        taskBonus: tb.value,
        standardHoursPerDay: sh.value,
        officeCheckInTime,
        weights,
      }
      await apiFetch('/api/config', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      toast.success('Saved. Existing monthly reports flagged as stale — regenerate to refresh.')
    } catch (e: any) { toast.error(e.message || 'Failed to save config') } finally { setLoading(false) }
  }

  const setWeight = (k: 'hours' | 'tasks' | 'ai', v: number) =>
    setWeights(w => ({ ...w, [k]: v }))

  const sumOk = Math.abs(sum - 1) <= 0.001

  return (
    // No PageShell: this only ever renders nested inside PerformanceAnalytics
    // or SettingsView, both of which already provide one.
    <div className="space-y-5 md:space-y-6">
      <SectionHeader icon={Settings} title="Stipend & Scoring Config" description="Adjust pay rates and performance scoring weights" />

      {fetching ? <TabLoader /> : (
        <>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <GlassCard tone="orange" inset>
          <div className="p-4 md:p-5 space-y-2">
            <div className="flex items-center gap-2 text-neutral-700 dark:text-white/70 text-xs uppercase tracking-wider">
              <IndianRupee className="h-3.5 w-3.5 text-orange-600 dark:text-orange-300" /> Hourly Rate
            </div>
            <Input type="number" inputMode="decimal" value={hourlyRate}
              onChange={e => setHourlyRate(e.target.value)} className="text-lg" />
            <p className="text-xs text-neutral-400 dark:text-white/40">₹ per working hour</p>
          </div>
        </GlassCard>
        <GlassCard tone="green" inset>
          <div className="p-4 md:p-5 space-y-2">
            <div className="flex items-center gap-2 text-neutral-700 dark:text-white/70 text-xs uppercase tracking-wider">
              <Award className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-300" /> Task Bonus
            </div>
            <Input type="number" inputMode="decimal" value={taskBonus}
              onChange={e => setTaskBonus(e.target.value)} className="text-lg" />
            <p className="text-xs text-neutral-400 dark:text-white/40">₹ per completed task</p>
          </div>
        </GlassCard>
        <GlassCard tone="blue" inset>
          <div className="p-4 md:p-5 space-y-2">
            <div className="flex items-center gap-2 text-neutral-700 dark:text-white/70 text-xs uppercase tracking-wider">
              <Clock className="h-3.5 w-3.5 text-blue-600 dark:text-blue-300" /> Standard Hours / Day
            </div>
            <Input type="number" inputMode="decimal" value={standardHoursPerDay}
              onChange={e => setStandardHoursPerDay(e.target.value)} className="text-lg" />
            <p className="text-xs text-neutral-400 dark:text-white/40">Used for expected-hours calc</p>
          </div>
        </GlassCard>
        <GlassCard tone="violet" inset>
          <div className="p-4 md:p-5 space-y-2">
            <div className="flex items-center gap-2 text-neutral-700 dark:text-white/70 text-xs uppercase tracking-wider">
              <Clock className="h-3.5 w-3.5 text-violet-600 dark:text-violet-300" /> Office Check-In Time
            </div>
            <Input type="time" value={officeCheckInTime} onChange={e => setOfficeCheckInTime(e.target.value)} className="text-lg" />
            <p className="text-xs text-neutral-400 dark:text-white/40">Employees may check in up to 11 minutes early.</p>
          </div>
        </GlassCard>
      </div>

      <GlassCard>
        <GlassCardHeader>
          <div>
            <GlassCardTitle>Score Weights</GlassCardTitle>
            <p className="text-xs text-neutral-400 dark:text-white/40 mt-1">Distribute weight across hours, tasks and AI evaluation (must sum to 1.0)</p>
          </div>
          <span className={`text-sm font-medium tabular-nums ${sumOk ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
            Σ {sum.toFixed(2)}
          </span>
        </GlassCardHeader>
        <div className="space-y-5">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-neutral-800 dark:text-white/80">Hours</Label>
              <span className="text-sm text-neutral-600 dark:text-white/60 tabular-nums">{weights.hours.toFixed(2)}</span>
            </div>
            <Slider value={[weights.hours]} onValueChange={v => setWeight('hours', v[0])} max={1} step={0.05} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-neutral-800 dark:text-white/80">Tasks</Label>
              <span className="text-sm text-neutral-600 dark:text-white/60 tabular-nums">{weights.tasks.toFixed(2)}</span>
            </div>
            <Slider value={[weights.tasks]} onValueChange={v => setWeight('tasks', v[0])} max={1} step={0.05} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-neutral-800 dark:text-white/80">AI Evaluation</Label>
              <span className="text-sm text-neutral-600 dark:text-white/60 tabular-nums">{weights.ai.toFixed(2)}</span>
            </div>
            <Slider value={[weights.ai]} onValueChange={v => setWeight('ai', v[0])} max={1} step={0.05} />
          </div>
          {!sumOk && <p className="text-xs text-rose-600 dark:text-rose-400">Weights must sum to 1.0</p>}
        </div>
      </GlassCard>

      <div className="flex justify-end">
        <Button onClick={save} disabled={loading} className={PRIMARY_BTN}>
          <Save className="h-4 w-4 mr-2" /> Save Config
        </Button>
      </div>
        </>
      )}
    </div>
  )
}
